import { IPAuthContext } from "../providers/ProductServerAuth";

export interface ListOptions {
  query?: string;
  q?: string;
  populate?: string;
  projection?: string;
  sort?: string;
  select?: string;
  skip?: number;
  limit?: number;
  p?: number;
  page?: number;
  pageSize?: number;
  totalCount?: boolean;
}

export class RestApi<T> {
  constructor(private baseUrl: string, private auth: IPAuthContext, private token?: string) { }
  private listCache: { [url: string]: Promise<{ list: T[]; totalCount?: number }> } = {};
  private singleCache: { [url: string]: Promise<T> } = {};

  async list(
    model: string,
    options?: ListOptions,
    additionalHeaders?: Record<string, string>
  ): Promise<{ list: T[]; totalCount?: number }> {
    // console.log("options", options);

    const queryParams = options
      ? new URLSearchParams(options as Record<string, string>).toString()
      : "";
    const url = `${this.baseUrl}${model}${queryParams ? `?${queryParams}` : ""}`;
    // console.log("url", url);
    const cacheKey = `${url}-${additionalHeaders && JSON.stringify(additionalHeaders)}`;

    if (!this.listCache[cacheKey]) {
      this.listCache[cacheKey] = this.fetchApi(url, "GET", additionalHeaders).then((response) => {
        // Extract total count from response headers
        const totalCountHeader = response.headers.get("x-total-count");
        const totalCount = totalCountHeader ? parseInt(totalCountHeader) : undefined;

        // Parse and return response body as list
        return response.json().then((listData) => {
          // Return an object containing the list and total count (if available)
          return { list: listData, totalCount };
        });
      });
    }
    return this.listCache[cacheKey];
  }

  async single(
    model: string,
    options?: ListOptions,
    additionalHeaders?: Record<string, string>
  ): Promise<T> {
    // console.log("exec single");
    const queryParams = options
      ? new URLSearchParams(options as Record<string, string>).toString()
      : "";
    const url = `${this.baseUrl}${model}${queryParams ? `?${queryParams}` : ""}`;
    const cacheKey = `${url}-${additionalHeaders && JSON.stringify(additionalHeaders)}`;

    if (!this.singleCache[cacheKey]) {
      this.singleCache[cacheKey] = this.fetchApi(url, "GET", additionalHeaders).then((r) => {
        // console.log("response before json: ", r);
        return r.json();
      });
    }
    return this.singleCache[cacheKey];
  }

  async post(
    model: string,
    additionalHeaders?: Record<string, string>,
    body?: Record<string, any>
  ): Promise<T> {
    const url = `${this.baseUrl}${model}`;
    const postBody = body ? body : {};
    return this.fetchApi(url, "POST", additionalHeaders, postBody).then((r) => {
      console.log("response before json: ", r);
      return r.json();
    });
  }

  async get(
    path: string,
    additionalHeaders?: Record<string, string>,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    return this.fetchApi(url, "GET", additionalHeaders).then((r) => {
      console.log("response before json: ", r);
      return r.json();
    });
  }

  async patch(
    model: string,
    body?: Record<string, any>,
    additionalHeaders?: Record<string, string>
  ): Promise<T> {
    const url = `${this.baseUrl}${model}`;
    const postBody = body ? body : {};
    return this.fetchApi(url, "PATCH", additionalHeaders, postBody).then((r) => {
      // console.log("response before json: ", r);
      return r.json();
    });
  }

  private async fetchApi(
    url: string,
    method: string,
    additionalHeaders?: Record<string, string>,
    body?: Record<string, any>
  ): Promise<Response> {
    // console.log("exec fetch : url", url);
    // console.log("method", method);
    // console.log("additionalHeaders: ", additionalHeaders);
    // console.log("body", body);

    try {
      // const token = this.auth.getSession()?.token;
      const currentToken = this.token;
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
          ...additionalHeaders,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
        if (response.status === 401) {
          // check if expired/invalid token, clear it
          if (currentToken) {
            this.auth.logout();
          }
          console.warn("Access Denied:", response.statusText);
        }
        let errorData;
        try {
          errorData = await response.json();
        } catch (err) {
          // Handle non-JSON responses
          errorData = { message: "Failed to parse response body" };
        }
        throw new Error(`Error: ${response.statusText} - ${JSON.stringify(errorData)}`);
      }
      return response;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }
}
